import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import {
    FaTachometerAlt, FaBox, FaPlus, FaEdit, FaTrash, FaTimes, FaSave,
    FaHourglassHalf, FaCheckCircle, FaTimesCircle
} from 'react-icons/fa';
import {
    getProviderGoods, createProviderGood, updateProviderGood, deleteProviderGood, getProviderStats, uploadFile
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n/LanguageContext';
import Loader from '../components/Loader';
import './Provider.css';

const T = {
    en: {
        uploadFailed: 'Upload failed',
        operationFailed: 'Operation failed',
        confirmDeleteGood: 'Are you sure you want to delete this good?',
        failedToDeleteGood: 'Failed to delete good',
        tabDashboard: 'Dashboard',
        tabMyGoods: 'My Goods',
        providerPanel: 'Provider Panel',
        loadingDashboard: 'Loading dashboard',
        welcome: (name) => `Welcome, ${name}`,
        providerSubtitle: 'Manage your goods and track their approval status.',
        totalGoods: 'Total Goods',
        pendingReview: 'Pending Review',
        approved: 'Approved',
        rejected: 'Rejected',
        myGoodsCount: (count) => `My Goods (${count})`,
        postNewGood: 'Post New Good',
        loadingGoods: 'Loading goods',
        colId: 'ID',
        colImage: 'Image',
        colName: 'Name',
        colCategory: 'Category',
        colPrice: 'Price',
        colQtyAvailable: 'Qty Available',
        colStatus: 'Status',
        colAdminNote: 'Admin Note',
        colActions: 'Actions',
        noImg: 'No img',
        statusPending: 'pending',
        statusApproved: 'approved',
        statusRejected: 'rejected',
        emptyGoods: 'No goods posted yet. Click "Post New Good" to get started!',
        editGood: 'Edit Good',
        productName: 'Product Name *',
        category: 'Category *',
        description: 'Description',
        priceLabel: 'Price (XAF) *',
        quantityAvailable: 'Quantity Available *',
        image: 'Image',
        uploading: 'Uploading',
        imageUrlPlaceholder: '...or paste a URL: https://... or /images/product.jpg',
        previewAlt: 'Preview',
        updating: 'Updating',
        posting: 'Posting',
        updateGood: 'Update Good',
        postGood: 'Post Good'
    },
    fr: {
        uploadFailed: "Echec du televersement",
        operationFailed: "Echec de l'operation",
        confirmDeleteGood: "Etes-vous sur de vouloir supprimer ce bien ?",
        failedToDeleteGood: "Echec de la suppression du bien",
        tabDashboard: "Tableau de bord",
        tabMyGoods: "Mes biens",
        providerPanel: "Panneau fournisseur",
        loadingDashboard: "Chargement du tableau de bord",
        welcome: (name) => `Bienvenue, ${name}`,
        providerSubtitle: "Gerez vos biens et suivez leur statut d'approbation.",
        totalGoods: "Total des biens",
        pendingReview: "En attente de validation",
        approved: "Approuves",
        rejected: "Rejetes",
        myGoodsCount: (count) => `Mes biens (${count})`,
        postNewGood: "Publier un nouveau bien",
        loadingGoods: "Chargement des biens",
        colId: "ID",
        colImage: "Image",
        colName: "Nom",
        colCategory: "Categorie",
        colPrice: "Prix",
        colQtyAvailable: "Qte disponible",
        colStatus: "Statut",
        colAdminNote: "Note de l'administrateur",
        colActions: "Actions",
        noImg: "Aucune img",
        statusPending: "en attente",
        statusApproved: "approuve",
        statusRejected: "rejete",
        emptyGoods: "Aucun bien publie pour le moment. Cliquez sur \"Publier un nouveau bien\" pour commencer !",
        editGood: "Modifier le bien",
        productName: "Nom du produit *",
        category: "Categorie *",
        description: "Description",
        priceLabel: "Prix (XAF) *",
        quantityAvailable: "Quantite disponible *",
        image: "Image",
        uploading: "Televersement",
        imageUrlPlaceholder: "...ou collez une URL : https://... ou /images/product.jpg",
        previewAlt: "Apercu",
        updating: "Mise a jour",
        posting: "Publication",
        updateGood: "Mettre a jour le bien",
        postGood: "Publier le bien"
    }
};

function Provider() {
    const t = useT(T);
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('dashboard');
    const [stats, setStats] = useState(null);
    const [goods, setGoods] = useState([]);
    const [loadingStats, setLoadingStats] = useState(false);
    const [loadingGoods, setLoadingGoods] = useState(false);
    const [submittingGood, setSubmittingGood] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);

    // Good form
    const [showForm, setShowForm] = useState(false);
    const [editingGood, setEditingGood] = useState(null);
    const [formData, setFormData] = useState({
        name: '', description: '', price: '', category: '', image_url: '', quantity_available: ''
    });

    useEffect(() => {
        if (user?.role === 'provider') {
            if (activeTab === 'dashboard') loadStats();
            if (activeTab === 'goods') loadGoods();
        }
    }, [activeTab, user]);

    if (!user || user.role !== 'provider') return <Navigate to="/" />;

    const loadStats = () => {
        setLoadingStats(true);
        return getProviderStats()
            .then(r => setStats(r.data))
            .catch(() => {})
            .finally(() => setLoadingStats(false));
    };
    const loadGoods = () => {
        setLoadingGoods(true);
        return getProviderGoods()
            .then(r => setGoods(r.data))
            .catch(() => {})
            .finally(() => setLoadingGoods(false));
    };

    const handleFileUpload = async (file) => {
        if (!file) return;
        setUploadingImage(true);
        try {
            const url = await uploadFile(file);
            setFormData(f => ({ ...f, image_url: url }));
        } catch (err) {
            alert(err.response?.data?.error || t.uploadFailed);
        } finally {
            setUploadingImage(false);
        }
    };

    const openCreateForm = () => {
        setEditingGood(null);
        setFormData({ name: '', description: '', price: '', category: '', image_url: '', quantity_available: '' });
        setShowForm(true);
    };

    const openEditForm = (good) => {
        setEditingGood(good);
        setFormData({
            name: good.name, description: good.description || '',
            price: good.price, category: good.category,
            image_url: good.image_url || '', quantity_available: good.quantity_available
        });
        setShowForm(true);
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setSubmittingGood(true);
        try {
            const data = {
                ...formData,
                price: Number(formData.price),
                quantity_available: Number(formData.quantity_available)
            };
            if (editingGood) {
                await updateProviderGood(editingGood.id, data);
            } else {
                await createProviderGood(data);
            }
            setShowForm(false);
            loadGoods();
        } catch (err) {
            alert(err.response?.data?.error || t.operationFailed);
        } finally {
            setSubmittingGood(false);
        }
    };

    const handleDeleteGood = async (id) => {
        if (!window.confirm(t.confirmDeleteGood)) return;
        try { await deleteProviderGood(id); loadGoods(); } catch { alert(t.failedToDeleteGood); }
    };

    const getStatusIcon = (status) => {
        if (status === 'pending') return <FaHourglassHalf className="status-icon pending" />;
        if (status === 'approved') return <FaCheckCircle className="status-icon approved" />;
        return <FaTimesCircle className="status-icon rejected" />;
    };

    const getStatusLabel = (status) => {
        if (status === 'pending') return t.statusPending;
        if (status === 'approved') return t.statusApproved;
        if (status === 'rejected') return t.statusRejected;
        return status;
    };

    const tabs = [
        { key: 'dashboard', label: t.tabDashboard, icon: <FaTachometerAlt /> },
        { key: 'goods', label: t.tabMyGoods, icon: <FaBox /> },
    ];

    return (
        <div className="admin-page">
            <div className="admin-sidebar provider-sidebar">
                <h2 className="admin-logo">{t.providerPanel}</h2>
                <nav className="admin-nav">
                    {tabs.map(tab => (
                        <button key={tab.key} className={`admin-nav-btn ${activeTab === tab.key ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.key)}>
                            {tab.icon} <span>{tab.label}</span>
                        </button>
                    ))}
                </nav>
            </div>

            <div className="admin-content">

                {/* DASHBOARD */}
                {activeTab === 'dashboard' && !stats && loadingStats && (
                    <Loader label={t.loadingDashboard} />
                )}
                {activeTab === 'dashboard' && stats && (
                    <div className="admin-dashboard">
                        <h1>{t.welcome(user.name)}</h1>
                        <p className="provider-subtitle">{t.providerSubtitle}</p>
                        <div className="stats-grid">
                            <div className="stat-card">
                                <FaBox className="stat-icon" />
                                <div><h3>{stats.totalGoods}</h3><p>{t.totalGoods}</p></div>
                            </div>
                            <div className="stat-card pending-card">
                                <FaHourglassHalf className="stat-icon" />
                                <div><h3>{stats.pendingGoods}</h3><p>{t.pendingReview}</p></div>
                            </div>
                            <div className="stat-card approved-card">
                                <FaCheckCircle className="stat-icon" />
                                <div><h3>{stats.approvedGoods}</h3><p>{t.approved}</p></div>
                            </div>
                            <div className="stat-card rejected-card">
                                <FaTimesCircle className="stat-icon" />
                                <div><h3>{stats.rejectedGoods}</h3><p>{t.rejected}</p></div>
                            </div>
                        </div>
                    </div>
                )}

                {/* GOODS */}
                {activeTab === 'goods' && (
                    <div className="admin-products">
                        <div className="admin-header">
                            <h1>{t.myGoodsCount(goods.length)}</h1>
                            <button className="admin-btn primary" onClick={openCreateForm}><FaPlus /> {t.postNewGood}</button>
                        </div>
                        {loadingGoods && goods.length === 0 && <Loader label={t.loadingGoods} />}
                        <table className="admin-table">
                            <thead>
                                <tr><th>{t.colId}</th><th>{t.colImage}</th><th>{t.colName}</th><th>{t.colCategory}</th><th>{t.colPrice}</th><th>{t.colQtyAvailable}</th><th>{t.colStatus}</th><th>{t.colAdminNote}</th><th>{t.colActions}</th></tr>
                            </thead>
                            <tbody>
                                {goods.map(g => (
                                    <tr key={g.id}>
                                        <td>#{g.id}</td>
                                        <td>
                                            {g.image_url
                                                ? <img src={g.image_url} alt={g.name} className="table-thumb" />
                                                : <span className="no-img">{t.noImg}</span>}
                                        </td>
                                        <td>{g.name}</td>
                                        <td><span className="category-tag">{g.category}</span></td>
                                        <td>{Number(g.price).toLocaleString()} XAF</td>
                                        <td>{g.quantity_available}</td>
                                        <td>
                                            <span className={`status-badge ${g.status}`}>
                                                {getStatusIcon(g.status)} {getStatusLabel(g.status)}
                                            </span>
                                        </td>
                                        <td className="admin-note-cell">{g.admin_note || '-'}</td>
                                        <td className="actions-cell">
                                            <button className="admin-btn small edit" onClick={() => openEditForm(g)}><FaEdit /></button>
                                            <button className="admin-btn small danger" onClick={() => handleDeleteGood(g.id)}><FaTrash /></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {goods.length === 0 && <p className="empty-state">{t.emptyGoods}</p>}

                        {/* Good form modal */}
                        {showForm && (
                            <div className="modal-overlay" onClick={() => setShowForm(false)}>
                                <div className="modal" onClick={e => e.stopPropagation()}>
                                    <div className="modal-header">
                                        <h2>{editingGood ? t.editGood : t.postNewGood}</h2>
                                        <button className="modal-close" onClick={() => setShowForm(false)}><FaTimes /></button>
                                    </div>
                                    <form onSubmit={handleFormSubmit} className="product-form">
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>{t.productName}</label>
                                                <input type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required />
                                            </div>
                                            <div className="form-group">
                                                <label>{t.category}</label>
                                                <input type="text" value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})} required />
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label>{t.description}</label>
                                            <textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} rows="3" />
                                        </div>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>{t.priceLabel}</label>
                                                <input type="number" value={formData.price} onChange={e => setFormData({...formData, price: e.target.value})} required min="0" />
                                            </div>
                                            <div className="form-group">
                                                <label>{t.quantityAvailable}</label>
                                                <input type="number" value={formData.quantity_available} onChange={e => setFormData({...formData, quantity_available: e.target.value})} required min="0" />
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label>{t.image}</label>
                                            <input type="file" accept="image/*" disabled={uploadingImage}
                                                onChange={e => handleFileUpload(e.target.files[0])} />
                                            {uploadingImage && <Loader inline label={t.uploading} />}
                                            <input type="text" value={formData.image_url} onChange={e => setFormData({...formData, image_url: e.target.value})} placeholder={t.imageUrlPlaceholder} />
                                            {formData.image_url && <img src={formData.image_url} alt={t.previewAlt} className="form-img-preview" />}
                                        </div>
                                        <button type="submit" className="admin-btn primary full-width" disabled={submittingGood}>
                                            {submittingGood
                                                ? <Loader inline label={editingGood ? t.updating : t.posting} />
                                                : <><FaSave /> {editingGood ? t.updateGood : t.postGood}</>}
                                        </button>
                                    </form>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default Provider;
